import {useAtom, useSetAtom} from 'jotai';
import {HTMLAttributes, ReactNode, TouchEvent, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {
  mobileBottomSheetExpandedAtom,
  mobileBottomSheetFullTopAtom,
  mobileBottomSheetTopAtom,
} from '@/map/atom';
import {interceptProps} from '@/styled-components';
import {FULL_SCREEN, LAYOUT_Z_INDEX, palette} from '@/styles';
import MapIcon from './map-icon.svg';

// 바텀시트 기본(접힘) top 위치. 외부(플로팅 버튼)에서 접힘 여부 판정에 재사용.
export const SHEET_COLLAPSED_TOP = 211;

// 시트 본문 스크롤 컨테이너. 자식 콘텐츠에서 스크롤 진행률을 측정할 때 참조한다.
export const SHEET_SCROLL_BODY_ID = 'myfc-mobile-bottom-sheet-scroll';

const sheetTopPosition = SHEET_COLLAPSED_TOP;

interface IProps extends HTMLAttributes<HTMLDivElement> {
  tabs?: ReactNode;
  hidden?: boolean;
}

export const BottomSheet = ({tabs, hidden, children}: IProps) => {
  const [topPosition, setTopPosition] = useAtom(mobileBottomSheetTopAtom);
  const setFullTop = useSetAtom(mobileBottomSheetFullTopAtom);
  const setExpanded = useSetAtom(mobileBottomSheetExpandedAtom);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const fullHeight = rootRef.current?.clientHeight;
  const isFullyOpened = !!fullHeight && topPosition >= fullHeight;

  const handleSlideUp = () => {
    if (rootRef.current?.clientHeight) {
      // 모바일 브라우저 비주얼 뷰포트 특성 때문에 topPosition 고정값 처리함
      setTopPosition(rootRef.current.clientHeight);
    }
  };

  const handleSlideDown = () => {
    setTouchStart(0);
    setTopPosition(sheetTopPosition);
  };

  const onTouchStart = (e: TouchEvent) => {
    const scrollTop = scrollContainerRef.current?.scrollTop;

    if (scrollTop !== 0) {
      return;
    }

    if (e.targetTouches) {
      setTouchStart(e.targetTouches[0].clientY);
      setTouchEnd(e.targetTouches[0].clientY);
    }
  };

  const onTouchMove = (e: TouchEvent) => {
    if (touchStart === 0) {
      setTouchEnd(0);
      return;
    }

    if (e.targetTouches) {
      setTouchEnd(e.targetTouches[0].clientY);
    }
  };

  const onTouchEnd = () => {
    const gap = touchStart - touchEnd;

    if (gap !== 0 && gap > 40) {
      handleSlideUp();
      return;
    }

    if (gap !== 0 && gap < -80) {
      handleSlideDown();
      return;
    }
    setTouchStart(0);
  };

  useEffect(() => {
    if (!topPosition) {
      setTopPosition(sheetTopPosition);
    }

    // 완전 확장 높이를 외부(플로팅 버튼)에서 참조할 수 있도록 저장
    if (rootRef.current?.clientHeight) {
      setFullTop(rootRef.current.clientHeight);
    }

    return () => {
      setTopPosition(sheetTopPosition);
      setExpanded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setExpanded(isFullyOpened);
  }, [isFullyOpened, setExpanded]);

  return (
    <Root
      ref={rootRef}
      hidden={hidden}
      topPosition={topPosition}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchMove={onTouchMove}
    >
      <HandleWrapper>
        <Handle />
      </HandleWrapper>
      {!!tabs && tabs}
      <ScrollBody
        id={SHEET_SCROLL_BODY_ID}
        ref={scrollContainerRef}
        fixedScroll={!isFullyOpened}
        data-fixed={!isFullyOpened}
      >
        {children}
      </ScrollBody>
      <SlideDownButton onClick={handleSlideDown}>
        <img src={MapIcon} alt="지도 보기" />
        지도 보기
      </SlideDownButton>
    </Root>
  );
};

const Root = styled('div').withConfig({
  shouldForwardProp: interceptProps(['topPosition', 'hidden']),
})<{
  topPosition?: number;
  hidden?: boolean;
}>`
  position: absolute;
  left: 0;
  right: 0;
  top: ${({topPosition}) => `calc(${FULL_SCREEN.vh} - ${topPosition}px)`};
  z-index: ${LAYOUT_Z_INDEX.NAVBAR};
  background-color: ${palette.white};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  transition: top 400ms;
  height: calc(${FULL_SCREEN.vh} - 60px);
  display: flex;
  flex-direction: column;
  ${({hidden}) => hidden && `visibility: hidden;`}
`;

const ScrollBody = styled('div').withConfig({
  shouldForwardProp: interceptProps(['fixedScroll']),
})<{
  fixedScroll?: boolean;
}>`
  ${({fixedScroll}) => !fixedScroll && `overflow-y: auto;`}
`;

const Handle = styled.div`
  width: 40px;
  height: 5px;
  flex-shrink: 0;
  background-color: ${palette.gray200};
  border-radius: 999px;
`;

const HandleWrapper = styled.div`
  display: flex;
  height: 28px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`;

const SlideDownButton = styled.button`
  position: absolute;
  height: 36px;
  width: 104px;
  bottom: 20px;
  left: 50%;
  margin-left: -52px;
  color: ${palette.white};
  background-color: ${palette.black};
  border-radius: 999px;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  font-style: normal;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;
